import { supabase } from '../lib/supabase.js';

/**
 * SMS Questionnaire Service for Twilio TCPA Compliant Communications
 * Handles four-field questionnaire submissions and SMS consent management
 */
export class SMSQuestionnaireService {
  /**
   * Submit SMS questionnaire response
   * @param {Object} data - Questionnaire data
   * @param {string} data.phoneNumber - Contact phone number
   * @param {string} data.detaineeName - Name of person in jail (🆕 CRITICAL FIELD)
   * @param {string} data.relationshipToDetainee - Relationship to detainee
   * @param {string} data.detaineeLocation - Detention facility
   * @param {string} data.urgencyLevel - Urgency level
   * @param {string} data.financialCapability - Financial capability
   * @param {boolean} data.smsConsent - SMS consent status
   * @param {boolean} data.termsAccepted - Terms acceptance
   * @param {boolean} data.privacyPolicyAccepted - Privacy policy acceptance
   * @param {Object} data.leadData - Optional lead data to upsert
   * @param {boolean} data.skipSMS - Skip sending SMS (if already sent via Vercel)
   */
  static async submitQuestionnaire(data) {
    try {
      const {
        phoneNumber,
        detaineeName, // 🆕 CRITICAL: Extract detainee name
        relationshipToDetainee,
        detaineeLocation,
        urgencyLevel,
        financialCapability,
        smsConsent,
        termsAccepted,
        privacyPolicyAccepted,
        leadData,
        skipSMS = false
      } = data;

      // First check if phone is opted out
      const isOptedOut = await SMSQuestionnaireService?.checkOptOutStatus(phoneNumber);
      if (isOptedOut) {
        throw new Error('This phone number has previously opted out of SMS communications');
      }

      // Use the new upsert function instead of problematic ON CONFLICT
      const { data: upsertResult, error: upsertError } = await supabase?.rpc('upsert_lead_by_phone', {
        lead_name: leadData?.name || 'SMS Questionnaire Submission',
        lead_phone: phoneNumber,
        lead_intent: 'bail_help',
        lead_status: 'new',
        lead_session_id: `sms_questionnaire_${Date.now()}`,
        lead_notes: `SMS questionnaire submission - ${relationshipToDetainee} needs help with ${detaineeLocation}`,
        lead_metadata: {
          source: 'sms_questionnaire',
          urgency: urgencyLevel,
          financial_range: financialCapability
        }
      });

      if (upsertError) {
        console.error('Lead upsert error:', upsertError);
        throw new Error('Failed to create lead record');
      }

      const leadId = upsertResult?.[0]?.lead_id;
      if (!leadId) {
        throw new Error('Lead creation returned invalid ID');
      }

      // Create questionnaire response record
      const questionnaireData = {
        phone_number: phoneNumber,
        detainee_name: detaineeName, // 🆕 CRITICAL: Store detainee name in database
        relationship_to_detainee: relationshipToDetainee,
        detainee_location: detaineeLocation,
        urgency_level: urgencyLevel,
        financial_capability: financialCapability,
        sms_consent_status: smsConsent ? 'opted_in' : 'pending',
        terms_accepted: termsAccepted,
        privacy_policy_accepted: privacyPolicyAccepted,
        consent_timestamp: smsConsent ? new Date()?.toISOString() : null,
        terms_accepted_timestamp: termsAccepted ? new Date()?.toISOString() : null,
        lead_id: leadId
      };

      const questionnaireResponse = await supabase?.from('sms_questionnaire_responses')?.insert(questionnaireData);
      if (questionnaireResponse?.error) {
        console.error('Questionnaire submission error:', questionnaireResponse?.error);
        throw new Error('Failed to submit questionnaire');
      }

      // 🆕 CRITICAL FIX: Capture SMS sending result and include in response
      let smsResult = { success: false, skipped: true };
      
      if (smsConsent && !skipSMS) {
        // 🚀 NEW: Send emergency agent notifications via dedicated Edge Function
        const agentNotificationResult = await SMSQuestionnaireService?.sendEmergencyAgentNotifications({
          fullName: leadData?.name || 'Emergency Contact',
          phoneNumber,
          detaineeName,
          relationshipToDetainee,
          detaineeLocation,
          urgencyLevel,
          financialCapability,
          smsConsent
        });

        // Send customer confirmation SMS
        const customerSmsResult = await SMSQuestionnaireService?.sendInitialContactSMS(phoneNumber, {
          detaineeName,
          relationshipToDetainee,
          urgencyLevel,
          detaineeLocation
        });

        // Combine both results
        smsResult = {
          success: agentNotificationResult?.success && customerSmsResult?.success,
          agentNotification: agentNotificationResult,
          customerConfirmation: customerSmsResult,
          requiresCredentialFix: agentNotificationResult?.requiresCredentialFix || customerSmsResult?.requiresCredentialFix,
          userMessage: !agentNotificationResult?.success 
            ? agentNotificationResult?.userMessage || '📱 Agent notifications temporarily unavailable. Your emergency request has been saved.'
            : customerSmsResult?.userMessage
        };
      } else if (skipSMS) {
        console.log('📱 SMSQuestionnaireService: Skipping SMS send - already handled by Vercel API');
        smsResult = { success: true, skipped: true, reason: 'handled_externally' };
      }

      return {
        success: true,
        leadId,
        questionnaireId: questionnaireResponse?.data?.id,
        isExistingLead: upsertResult?.[0]?.is_new_lead === false,
        message: 'Questionnaire submitted successfully. A licensed agent will contact you in 5 minutes or less.',
        smsResult // Return complete SMS result including agent notifications
      };

    } catch (error) {
      console.error('SMS Questionnaire submission error:', error);
      throw error;
    }
  }

  /**
   * 🔧 PRODUCTION FIX: Send emergency agent notifications using existing send-sms Edge Function
   * This function calls the send-sms Edge Function with sendToAgents flag which:
   * - Reads agent phones from Supabase secrets (EMERGENCY_AGENT_PHONE_1, EMERGENCY_AGENT_PHONE_2)
   * - Sends SMS to both emergency agents
   * - Returns success/failure status with detailed results
   */
  static async sendEmergencyAgentNotifications(emergencyData) {
    try {
      console.log('📱 SMSQuestionnaireService: Sending emergency agent notifications via send-sms Edge Function');

      const {
        fullName,
        phoneNumber,
        detaineeName,
        relationshipToDetainee,
        detaineeLocation,
        urgencyLevel,
        financialCapability
      } = emergencyData;

      // Build emergency message for agents
      const agentMessage = `🚨 EMERGENCY BAIL BOND REQUEST

Contact: ${fullName}
Phone: ${phoneNumber}
Detainee: ${detaineeName}
Relationship: ${relationshipToDetainee}
Location: ${detaineeLocation}
Urgency: ${urgencyLevel}
Budget: ${financialCapability}

CALL IMMEDIATELY: ${phoneNumber}`;

      // 🚀 CRITICAL: Use existing send-sms Edge Function with sendToAgents flag
      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          sendToAgents: true, // 🆕 FLAG: Triggers multi-agent SMS sending
          message: agentMessage
        })
      });

      let result;
      try {
        result = await response?.json();
      } catch (parseError) {
        console.error('❌ Failed to parse Edge Function response', { 
          status: response?.status,
          statusText: response?.statusText
        });
        return { 
          success: false, 
          error: `Edge Function returned status ${response?.status}`,
          userMessage: '📱 Emergency agent notifications temporarily unavailable. Your request has been saved.'
        };
      }

      if (!response?.ok) {
        console.error('❌ Emergency notification Edge Function error', { 
          status: response?.status,
          error: result
        });

        // Detect configuration issues
        const requiresAgentPhoneConfig = result?.error?.includes?.('agent phones not configured') || 
                                         result?.details?.requiredSecrets?.length > 0;
        const requiresCredentialFix = result?.error?.includes?.('Twilio configuration incomplete');

        return { 
          success: false, 
          error: result?.error || `Edge Function error: ${response?.status}`,
          requiresAgentPhoneConfig,
          requiresCredentialFix,
          userMessage: requiresAgentPhoneConfig
            ? '📱 Agent phone numbers not configured. Your emergency request has been saved and our team will contact you directly.' :'📱 Emergency agent notifications temporarily unavailable. Your request has been saved.',
          technicalAction: requiresAgentPhoneConfig
            ? '🔧 ADD EMERGENCY_AGENT_PHONE_1 and EMERGENCY_AGENT_PHONE_2 to Supabase Edge Function secrets'
            : requiresCredentialFix
              ? '🔧 UPDATE Twilio credentials in Supabase Edge Function secrets' :'Check Edge Function logs for detailed error'
        };
      }

      console.log('✅ Emergency agent notifications sent successfully', {
        recipients: result?.recipients,
        totalAgents: result?.results?.length
      });

      return { 
        success: true,
        recipients: result?.recipients,
        results: result?.results,
        message: result?.message
      };

    } catch (error) {
      console.error('❌ Emergency agent notification error:', error);
      return { 
        success: false, 
        error: error?.message,
        userMessage: '📱 Emergency agent notifications temporarily unavailable. Your request has been saved and our team will contact you directly.',
        technicalAction: 'Check network connection and Supabase Edge Function deployment'
      };
    }
  }

  /**
   * Send initial contact SMS after questionnaire submission
   */
  static async sendInitialContactSMS(phoneNumber, questionnaireData) {
    try {
      const { relationshipToDetainee, urgencyLevel, detaineeLocation, detaineeName } = questionnaireData;
      
      // 🔧 CRITICAL FIX: Validate phone number before sending
      if (!phoneNumber || phoneNumber?.trim() === '') {
        console.error('❌ SMSQuestionnaireService: Phone number is empty or undefined');
        return { success: false, error: 'Phone number is required for SMS sending' };
      }
      
      // 🆕 CRITICAL FIX: Include detainee name in SMS message + UPDATED PHONE NUMBER
      const message = detaineeName 
        ? `Hello! We received your bail bond request for ${detaineeName} (your ${relationshipToDetainee?.toLowerCase()}) at ${detaineeLocation}. A licensed agent from Sailors Bail Bonds will contact you within 5 minutes at (916) 930-6312. Reply STOP to opt out.`
        : `Hello! We received your bail bond request for your ${relationshipToDetainee?.toLowerCase()} at ${detaineeLocation}. A licensed agent from Sailors Bail Bonds will contact you within 5 minutes at (916) 930-6312. Reply STOP to opt out.`;

      // 🚀 CRITICAL FIX: Create properly structured request payload as object (NOT stringified)
      const requestPayload = {
        to: phoneNumber, // Must include phone number in 'to' field
        message,
        metadata: {
          type: 'questionnaire_confirmation',
          relationship: relationshipToDetainee,
          urgency: urgencyLevel,
          location: detaineeLocation,
          detaineeName: detaineeName || 'Not provided' // Include detainee name in metadata
        }
      };
      
      console.log('📱 SMSQuestionnaireService: Sending follow-up SMS', {
        to: phoneNumber,
        hasPhoneNumber: !!phoneNumber,
        phoneLength: phoneNumber?.length,
        detaineeName, // Log detainee name for debugging
        requestPayloadPreview: {
          to: phoneNumber,
          messageLength: message?.length,
          hasMetadata: !!requestPayload?.metadata
        }
      });

      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(requestPayload) // ✅ JSON.stringify correctly converts object to JSON string
      });

      // 🔧 FIX: Parse response even if it fails to get detailed error info
      let result;
      try {
        result = await response?.json();
      } catch (parseError) {
        console.error('❌ SMSQuestionnaireService: Failed to parse response', { 
          status: response?.status,
          statusText: response?.statusText,
          parseError: parseError?.message
        });
        return { 
          success: false, 
          error: `SMS API returned status ${response?.status} - ${response?.statusText}`,
          requiresCredentialFix: response?.status === 401 || response?.status === 500
        };
      }
      
      if (!response?.ok) {
        console.error('❌ SMSQuestionnaireService: SMS sending failed', { 
          status: response?.status,
          error: result,
          sentPayload: {
            to: phoneNumber,
            messageLength: message?.length,
            metadataType: requestPayload?.metadata?.type
          }
        });
        
        // 🆕 CRITICAL: Detect Twilio credential errors and provide actionable feedback
        const isTwilioCredentialError = result?.error?.includes?.('authentication') || 
                                       result?.error?.includes?.('invalid') ||
                                       result?.details?.credential_validation_failed === true ||
                                       response?.status === 401;
        
        return { 
          success: false, 
          error: result?.error || `SMS sending failed with status ${response?.status}`,
          requiresCredentialFix: isTwilioCredentialError,
          twilioErrorDetails: result?.details || null,
          userMessage: isTwilioCredentialError 
            ? '📱 SMS notifications are temporarily unavailable due to a technical issue. Your emergency request has been saved successfully and our team will contact you directly by phone.'
            : 'SMS notification could not be sent, but your emergency request was saved successfully.'
        };
      }

      console.log('✅ SMSQuestionnaireService: Follow-up SMS sent successfully', result);
      return { success: true, messageSid: result?.messageSid };

    } catch (error) {
      console.error('❌ SMSQuestionnaireService: SMS sending error:', error);
      return { 
        success: false, 
        error: error?.message,
        userMessage: 'SMS notification could not be sent, but your emergency request was saved successfully.'
      };
    }
  }

  /**
   * Check if phone number is opted out
   */
  static async checkOptOutStatus(phoneNumber) {
    try {
      const { data, error } = await supabase?.rpc('is_phone_opted_out', { check_phone: phoneNumber });

      if (error) {
        console.error('Opt-out check error:', error);
        return false; // Allow if check fails
      }

      return data === true;
    } catch (error) {
      console.error('Opt-out status check error:', error);
      return false; // Allow if check fails
    }
  }

  /**
   * Process SMS opt-out (STOP message)
   */
  static async processOptOut(phoneNumber, keyword = 'STOP', messageSid = null) {
    try {
      const { error } = await supabase?.rpc('process_sms_opt_out', {
          opt_out_phone: phoneNumber,
          keyword,
          message_sid: messageSid
        });

      if (error) {
        console.error('Opt-out processing error:', error);
        throw new Error('Failed to process opt-out request');
      }

      // Send confirmation SMS
      const confirmationMessage = 'You have been successfully removed from our SMS list. You will not receive further messages from Sailors Bail Bonds.';
      
      await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: confirmationMessage,
          metadata: { type: 'opt_out_confirmation' }
        })
      });

      return { success: true, message: 'Opt-out processed successfully' };

    } catch (error) {
      console.error('Opt-out processing error:', error);
      throw error;
    }
  }

  /**
   * Get legal documents (privacy policy, terms of service)
   */
  static async getLegalDocuments() {
    try {
      const { data, error } = await supabase?.from('legal_documents')?.select('*')?.eq('is_active', true)?.order('document_type', { ascending: true });

      if (error) {
        console.error('Legal documents fetch error:', error);
        throw new Error('Failed to fetch legal documents');
      }

      return data || [];
    } catch (error) {
      console.error('Legal documents error:', error);
      throw error;
    }
  }

  /**
   * Get questionnaire responses (admin only)
   */
  static async getQuestionnaireResponses(filters = {}) {
    try {
      let query = supabase?.from('sms_questionnaire_responses')?.select(`
          *,
          lead:leads(
            id,
            name,
            email,
            phone,
            status,
            intent,
            created_at
          )
        `)?.order('created_at', { ascending: false });

      if (filters?.consentStatus) {
        query = query?.eq('sms_consent_status', filters?.consentStatus);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Questionnaire responses fetch error:', error);
        throw new Error('Failed to fetch questionnaire responses');
      }

      return data || [];
    } catch (error) {
      console.error('Questionnaire responses error:', error);
      throw error;
    }
  }

  /**
   * Get SMS opt-out records (admin only)
   */
  static async getOptOutRecords(filters = {}) {
    try {
      let query = supabase?.from('sms_opt_outs')?.select(`
          *,
          lead:leads(
            id,
            name,
            phone,
            status
          )
        `)?.order('opt_out_timestamp', { ascending: false });

      if (filters?.dateFrom) {
        query = query?.gte('opt_out_timestamp', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('opt_out_timestamp', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Opt-out records fetch error:', error);
        throw new Error('Failed to fetch opt-out records');
      }

      return data || [];
    } catch (error) {
      console.error('Opt-out records error:', error);
      throw error;
    }
  }

  /**
   * Send follow-up SMS (for agents)
   */
  static async sendFollowUpSMS(phoneNumber, message, metadata = {}) {
    try {
      // Check opt-out status first
      const isOptedOut = await this.checkOptOutStatus(phoneNumber);
      if (isOptedOut) {
        throw new Error('Cannot send SMS: Phone number has opted out');
      }

      const response = await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          to: phoneNumber,
          message: `${message} Reply STOP to opt out.`,
          metadata: {
            type: 'follow_up',
            ...metadata
          }
        })
      });

      let result = await response?.json();
      
      if (!response?.ok) {
        console.error('Follow-up SMS failed:', result);
        throw new Error(result?.error || 'Failed to send follow-up SMS');
      }

      return { success: true, messageSid: result?.messageSid };

    } catch (error) {
      console.error('Follow-up SMS error:', error);
      throw error;
    }
  }
}

export default SMSQuestionnaireService;